import { TaxesAndFeesResponse } from 'containers/invoices/invoices.model';
import {
  InvoiceLevelPaymentModes,
  selectAvailablePaymentModes,
  selectInvoiceCustomer,
  selectInvoiceFinalAmount,
  selectInvoiceLineItems,
  selectInvoicePaymentModes,
  selectNoPaymentMethodsAvailable,
  selectPartialPaymentEnabled,
  selectTipEnabled,
  selectTipTiming,
  togglePartialPaymentEnabled,
  toggleTipEnabled,
  updateInvoicePaymentModes,
  updateTipEnabled,
  updateTipTiming
} from 'containers/invoices/InvoicesSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect } from 'react';
import { InvoiceTabEnum } from '../constants';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import { CleverTapEventsInvoices } from 'containers/invoices/events';
import InvoicePaymentMethodsSection from './InvoicePaymentMethodsSection';
import { TipTimingEnum } from 'containers/invoices/constants';
import OtherOptionsSection from './OtherOptionsSection';
import { selectMerchantCustomizations } from 'containers/app/appSlice';

interface PaymentOptionsSectionProps {
  className?: string;
  taxesAndFees?: TaxesAndFeesResponse;
  context: React.Context<any>;
}

const PaymentOptionsSection: FC<PaymentOptionsSectionProps> = ({ taxesAndFees = null, context }) => {
  const tipEnabledState = useAppSelector(selectTipEnabled);
  const paymentModes = useAppSelector(selectInvoicePaymentModes);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmount);
  const noPaymentMethodsAvailable = useAppSelector(selectNoPaymentMethodsAvailable);
  const availablePaymentModes = useAppSelector(selectAvailablePaymentModes);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabled);
  const tipTiming = useAppSelector(selectTipTiming);
  const merchantCustomizations = useAppSelector(selectMerchantCustomizations);
  const invoiceCustomer = useAppSelector(selectInvoiceCustomer);
  const invoiceLineItems = useAppSelector(selectInvoiceLineItems);
  const showTipsSection = merchantCustomizations.tip.enabled;
  const dispatch = useAppDispatch();
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.PAYMENT_OPTIONS;

  useEffect(() => {
    // api call is unnecessary during edit invoice
    // Only set tip from settings if invoice is fresh (no user data)
    const hasUserData = invoiceCustomer !== null || invoiceLineItems.length > 0;
    if (taxesAndFees && !hasUserData) {
      dispatch(updateTipEnabled(taxesAndFees?.tip?.enabled));
    }
  }, [taxesAndFees, invoiceCustomer, invoiceLineItems, dispatch]);

  const onTipConfigChange = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(toggleTipEnabled());
  };

  const onTipsLearnMoreClick = () => {
    openScanpayFAQInNewTab(CleverTapEventsInvoices.webInvoicesCreateInvoiceTipsFaqClick);
  };

  const handleUpdatePaymentModes = (updatedPaymentModes: InvoiceLevelPaymentModes[]) => {
    dispatch(updateInvoicePaymentModes(updatedPaymentModes));
  };

  const onPartialPaymentToggle = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(togglePartialPaymentEnabled());
  };

  const onTipTimingChange = (selectedOption: { id: TipTimingEnum; label: string }) => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(updateTipTiming(selectedOption.id));
  };

  return (
    <div>
      <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Payment options
      </div>
      <div
        className={`flex flex-col gap-[30px] rounded-md border px-[30px] py-5 1439px:p-10 ${
          isTabSelected ? 'border-primaryBtn' : 'border-borderGray'
        }`}>
        <div>
          <InvoicePaymentMethodsSection
            paymentModes={paymentModes}
            onUpdateInvoicePaymentModes={handleUpdatePaymentModes}
            invoiceTotal={invoiceTotal}
            noPaymentMethods={noPaymentMethodsAvailable}
            availablePaymentModes={availablePaymentModes}
            partialPaymentEnabled={partialPaymentEnabled}
          />
        </div>

        <OtherOptionsSection
          partialPaymentEnabled={partialPaymentEnabled}
          onPartialPaymentToggle={onPartialPaymentToggle}
          tipEnabled={tipEnabledState}
          onTipToggle={onTipConfigChange}
          onTipsLearnMoreClick={onTipsLearnMoreClick}
          tipTiming={tipTiming}
          onTipTimingChange={onTipTimingChange}
          isEdit={false}
          showTipsSection={showTipsSection}
        />
      </div>
    </div>
  );
};

export default PaymentOptionsSection;
